import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import SearchField from '../components/SearchField';
import MediaPreview from '../components/MediaPreview';
import MediaPoolDetail from '../components/MediaPoolDetail';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { deleteMediaPoolItems, getMediaPool } from '../api';
import { formatBytes, mediaDisplayName } from '../utils/mediaFormat';

const PAGE_SIZE = 48;
const USAGE_FILTERS = ['all', 'used', 'unused'];
const TYPE_FILTERS = ['', 'image', 'video', 'audio', 'other'];
const SORTS = ['newest', 'oldest', 'largest', 'name'];
const SEARCH_MIN_LENGTH = 2;

/**
 * Every stored file in one place: what it is, where it is used, and which
 * files are used nowhere - those can be deleted, one at a time or together.
 * Behind `media.manage`, platform-wide: the pool shows every department's files.
 */
function MediaPoolPage() {
  const { t, i18n } = useTranslation();

  const [usage, setUsage] = useState('all');
  const [type, setType] = useState('');
  const [sort, setSort] = useState('newest');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [openMediaId, setOpenMediaId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isConfirmingBulk, setConfirmingBulk] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    const next = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : '';
    const timer = window.setTimeout(() => setDebouncedQuery(next), 250);

    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(
    async (offset = 0) => {
      try {
        setIsLoading(true);
        setError('');

        const response = await getMediaPool({
          usage,
          type,
          sort,
          q: debouncedQuery,
          limit: PAGE_SIZE,
          offset,
        });

        setItems((current) =>
          offset === 0 ? response.items : [...current, ...response.items],
        );
        setTotal(response.total);
        setSummary(response.summary);
      } catch (loadError) {
        console.error('Failed to load media pool:', loadError);
        setError(loadError?.message || t('adminPanel.errorGeneric'));
      } finally {
        setIsLoading(false);
      }
    },
    [usage, type, sort, debouncedQuery, t],
  );

  useEffect(() => {
    setSelectedIds([]);
    setConfirmingBulk(false);
    load(0);
  }, [load]);

  function toggleSelected(mediaId) {
    setConfirmingBulk(false);
    setSelectedIds((current) =>
      current.includes(mediaId)
        ? current.filter((id) => id !== mediaId)
        : [...current, mediaId],
    );
  }

  const unusedOnPage = items.filter((item) => item.usageCount === 0);
  const allUnusedSelected =
    unusedOnPage.length > 0 &&
    unusedOnPage.every((item) => selectedIds.includes(item.id));

  function toggleAllUnused() {
    setConfirmingBulk(false);
    setSelectedIds(allUnusedSelected ? [] : unusedOnPage.map((item) => item.id));
  }

  function handleDeleted(deletedIds, skippedCount = 0) {
    setOpenMediaId(null);
    setSelectedIds([]);
    setConfirmingBulk(false);
    setNotice(
      [
        t('mediaPool.deletedNotice', { count: deletedIds.length }),
        skippedCount > 0
          ? t('mediaPool.skippedNotice', { count: skippedCount })
          : '',
      ]
        .filter(Boolean)
        .join(' '),
    );
    load(0);
  }

  async function deleteSelected() {
    try {
      setIsDeleting(true);
      setError('');

      const { deleted, skipped } = await deleteMediaPoolItems(selectedIds);

      handleDeleted(deleted, skipped.length);
    } catch (deleteError) {
      console.error('Bulk delete failed:', deleteError);
      setError(deleteError?.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsDeleting(false);
    }
  }

  const sizeOf = (bytes) => formatBytes(bytes, i18n.language);

  return (
    <PageLayout
      extraSidebarItems={[
        {
          to: '/admin/media',
          label: t('mediaPool.pageTitle'),
          icon: 'fa-solid fa-photo-film',
        },
      ]}
    >
      <PageMain>
        <PageHeader
          eyebrow={t('mediaPool.eyebrow')}
          title={t('mediaPool.pageTitle')}
          titleId="media-pool-title"
          description={t('mediaPool.pageDescription')}
        />

        <PageBody className="media-pool" aria-labelledby="media-pool-title">
          {summary && (
            <div className="media-pool-summary">
              <button
                type="button"
                className={`media-pool-stat${usage === 'all' ? ' active' : ''}`}
                onClick={() => setUsage('all')}
              >
                <strong>{summary.total}</strong>
                <span>
                  {t('mediaPool.statTotal')} · {sizeOf(summary.totalSize)}
                </span>
              </button>

              <button
                type="button"
                className={`media-pool-stat media-pool-stat-unused${usage === 'unused' ? ' active' : ''}`}
                onClick={() => setUsage('unused')}
              >
                <strong>{summary.unused}</strong>
                <span>
                  {t('mediaPool.statUnused')} · {sizeOf(summary.unusedSize)}
                </span>
              </button>
            </div>
          )}

          <div className="media-pool-toolbar">
            <div
              className="media-pool-segments"
              role="group"
              aria-label={t('mediaPool.filterUsage')}
            >
              {USAGE_FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={usage === value ? 'active' : ''}
                  aria-pressed={usage === value}
                  onClick={() => setUsage(value)}
                >
                  {t(`mediaPool.usageFilter.${value}`)}
                </button>
              ))}
            </div>

            <label className="media-pool-select">
              <span>{t('mediaPool.filterType')}</span>
              <select value={type} onChange={(event) => setType(event.target.value)}>
                {TYPE_FILTERS.map((value) => (
                  <option key={value || 'all'} value={value}>
                    {t(`mediaPool.typeFilter.${value || 'all'}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="media-pool-select">
              <span>{t('mediaPool.sortLabel')}</span>
              <select value={sort} onChange={(event) => setSort(event.target.value)}>
                {SORTS.map((value) => (
                  <option key={value} value={value}>
                    {t(`mediaPool.sort.${value}`)}
                  </option>
                ))}
              </select>
            </label>

            <SearchField
              value={query}
              onChange={setQuery}
              minLength={SEARCH_MIN_LENGTH}
              placeholder={t('mediaPool.searchPlaceholder')}
              ariaLabel={t('mediaPool.searchPlaceholder')}
            />
          </div>

          {notice && (
            <div className="login-message login-message-success" role="status">
              <i className="fa-solid fa-circle-check" />
              <span>{notice}</span>
            </div>
          )}

          {error && (
            <div className="login-message login-message-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" />
              <span>{error}</span>
            </div>
          )}

          {unusedOnPage.length > 0 && (
            <div className="media-pool-bulk">
              <label>
                <input
                  type="checkbox"
                  checked={allUnusedSelected}
                  onChange={toggleAllUnused}
                />
                {t('mediaPool.selectAllUnused', { count: unusedOnPage.length })}
              </label>

              {selectedIds.length > 0 &&
                (isConfirmingBulk ? (
                  <div className="admin-confirm-row">
                    <span>
                      {t('mediaPool.bulkConfirm', { count: selectedIds.length })}
                    </span>
                    <Button
                      variant="secondary"
                      disabled={isDeleting}
                      onClick={() => setConfirmingBulk(false)}
                    >
                      {t('adminPanel.cancel')}
                    </Button>
                    <Button
                      variant="secondary"
                      className="admin-danger-button"
                      disabled={isDeleting}
                      onClick={deleteSelected}
                    >
                      {isDeleting
                        ? t('adminPanel.deleting')
                        : t('adminPanel.deleteConfirmButton')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className="admin-danger-button"
                    onClick={() => setConfirmingBulk(true)}
                  >
                    <i className="fa-regular fa-trash-can" />
                    {t('mediaPool.deleteSelected', { count: selectedIds.length })}
                  </Button>
                ))}
            </div>
          )}

          {!isLoading && items.length === 0 && !error && (
            <p className="media-pool-empty">
              {usage === 'unused'
                ? t('mediaPool.emptyUnused')
                : t('mediaPool.empty')}
            </p>
          )}

          <ul className="media-pool-grid">
            {items.map((item) => {
              const isUnused = item.usageCount === 0;
              const isSelected = selectedIds.includes(item.id);

              return (
                <li
                  key={item.id}
                  className={`media-pool-card${isSelected ? ' selected' : ''}`}
                >
                  <button
                    type="button"
                    className="media-pool-card-open"
                    onClick={() => setOpenMediaId(item.id)}
                  >
                    <span className="media-pool-thumb">
                      <MediaPreview media={item} />
                    </span>

                    <span className="media-pool-card-body">
                      <strong title={mediaDisplayName(item)}>
                        {mediaDisplayName(item)}
                      </strong>
                      <small>
                        {(item.extension || '').toUpperCase()} · {sizeOf(item.size)}
                      </small>
                    </span>
                  </button>

                  <span
                    className={`media-pool-usage${isUnused ? ' unused' : ''}`}
                  >
                    {isUnused
                      ? t('mediaPool.unused')
                      : t('mediaPool.usedCount', { count: item.usageCount })}
                  </span>

                  {isUnused && (
                    <input
                      type="checkbox"
                      className="media-pool-check"
                      checked={isSelected}
                      aria-label={t('mediaPool.selectFile', {
                        name: mediaDisplayName(item),
                      })}
                      onChange={() => toggleSelected(item.id)}
                    />
                  )}
                </li>
              );
            })}
          </ul>

          {items.length < total && (
            <div className="media-pool-more">
              <Button
                variant="secondary"
                disabled={isLoading}
                onClick={() => load(items.length)}
              >
                {isLoading
                  ? t('mediaPool.loading')
                  : t('mediaPool.loadMore', {
                      shown: items.length,
                      total,
                    })}
              </Button>
            </div>
          )}
        </PageBody>
      </PageMain>

      {openMediaId !== null && (
        <MediaPoolDetail
          mediaId={openMediaId}
          onClose={() => setOpenMediaId(null)}
          onDeleted={(ids) => handleDeleted(ids)}
        />
      )}
    </PageLayout>
  );
}

export default MediaPoolPage;
